'use client';

import type {
  CallbackSettingsInput,
  ContactInput,
  FormCompletionContent,
  FormLeadStatus,
  FormLocale,
} from '@jlg-website/shared';
import QualifiedMessage from './qualified-message';
import NotQualifiedMessage from './not-qualified-message';
import DuplicatedMessage from './duplicated-message';
import { CallbackScheduler } from '../callback';
import type { CompletionAction, ConfirmCallback } from '../callback/shared/type';
import { showsCallbackScheduler } from './shows-callback-scheduler';

type ScheduleCallbackOption = {
  settings: CallbackSettingsInput;
  contact: ContactInput;
  locale?: FormLocale;
  onConfirm?: ConfirmCallback;
  /** Own submit path of the call-now flow — the backend treats it differently from a slot. */
  onCallNow?: ConfirmCallback;
  /** The form's closing button, which the scheduler only offers once a call back is booked. */
  completionAction?: CompletionAction;
};

/**
 * Builds the `renderCompletion` callback the FormRenderer calls once a lead settles. Mirrors the
 * renderer's precedence: duplicated wins over qualified, which wins over not-qualified. Returns
 * `undefined` when no message is resolved, so the renderer falls back to its legacy path.
 *
 * A qualified lead sees the callback scheduler instead of its message when the form has the
 * option switched on and the claim section publishes business hours.
 */
export function buildRenderCompletion(
  completion?: FormCompletionContent,
  schedule?: ScheduleCallbackOption | null
) {
  if (!completion && !schedule) return undefined;

  return function renderCompletion(status: FormLeadStatus) {
    // A duplicate lead is already in the pipeline, so it keeps its message and never reaches
    // the scheduler.
    if (status.is_duplicate) {
      return completion ? <DuplicatedMessage data={completion.duplicated} /> : null;
    }

    if (showsCallbackScheduler(schedule, status)) {
      return (
        <CallbackScheduler
          settings={schedule.settings}
          phone={schedule.contact.phone}
          zipCode={schedule.contact.zip_code}
          locale={schedule.locale}
          onConfirm={schedule.onConfirm}
          onCallNow={schedule.onCallNow}
          completionAction={schedule.completionAction}
        />
      );
    }

    if (!completion) return null;
    if (status.is_qualified) return <QualifiedMessage data={completion.qualified} />;

    return <NotQualifiedMessage data={completion.not_qualified} />;
  };
}
